import { React, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import {
  AllinedPageContainer,
  InsidePageContainer,
  OrderStatusLabel,
  RedLinkButton,
  StyledAccordion,
  StyledAccordionSummary,
  TicketBoughtCard,
  parseDevelonProducts,
} from '../common';
import {
  AccordionDetails,
  Button,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoaderCountContext } from 'context/LoaderV5';
import { useNetwork } from 'services/network';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ModeEditOutlineOutlined } from '@mui/icons-material';
import { format } from 'date-fns';
import EditTicketDatesDialog from '../common/EditTicketDatesDialog';
import { useTicketsSelector, useTicketsStore } from '../store/ticketsStore';
import { backHomeLink } from 'utils/constants';
import { SdmSpacer } from 'components';

export const WhitePageContainer = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  padding: '30px 0px',
  [`@media (max-width:${theme.breakpoints.values.lg}px)`]: {
    padding: '40px 0px',
  },
}));

const resultsPerPage = 5;

const PersonalOrders = ({ loggedUser, locale }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Tickets(intl).myTickets;
  const i18nCommon = translations.TicketsV2(intl).Common;
  const i18nLegacy = translations.Tickets(intl).myTickets.order;
  const { isLoaderActive, setLoaderActive } = useContext(LoaderCountContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const network = useNetwork();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { ticketToEditDates } = useTicketsSelector();

  // States
  const [orderPage, setOrderPage] = useState(0);
  const [orders, setOrders] = useState([]);
  const [orderAccordionsOpens, setOrderAccordionOpens] = useState([]);
  const [ordersTickets, setOrdersTickets] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Effects
  useEffect(() => {
    const fetchOrders = async () => {
      showLoader();
      const data = await network.ticketingLambda({
        params: `/orders?size=${resultsPerPage}&sort_by=updated_at&page=${orderPage}&sort_order=DESC&customer_email=${loggedUser.email.replace(
          '+',
          '%2b'
        )}`,
        customMethod: 'get',
        data: {},
        federationId: loggedUser?.salId,
      });
      if (data && !data.length) {
        hideLoader();
        return setOrderPage(-1);
      }
      if (!data.error) {
        const _filtered = data
          .filter((o) => !!o.payment_info)
          .map((o) => {
            const isPending = o?.status === 'pending';
            return {
              ...o,
              isPending,
            };
          });
        const _sorted = _filtered.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        );
        if (!orders.length && _sorted.length) {
          setOrderAccordionOpens([_sorted[0].id]);
        } else {
          hideLoader();
        }
        setOrders((prev) => [...prev, ..._sorted]);
      } else {
        setOrders([]);
        hideLoader();
      }
    };
    if (loggedUser && orderPage >= 0) fetchOrders();
  }, [loggedUser, orderPage]);

  useEffect(() => {
    const tryFetchOrderTickets = async () => {
      const notFetchedYet = orderAccordionsOpens.find(
        (orderId) => !Object.keys(ordersTickets).includes(orderId)
      );
      if (notFetchedYet) {
        showLoader();
        const data = await network.ticketingLambda({
          params: `/tickets?order_id=${notFetchedYet}`,
          customMethod: 'get',
          data: {},
          federationId: loggedUser?.salId,
        });
        const promises = [];
        data.map((ticket) => promises.push(getTicketsAvailableDates(ticket)));
        if (promises.length) {
          const slots = await Promise.all(promises);
          const ticketsWithSlot = slots.map((slot, idx) => ({
            ...data[idx],
            slots: slot,
          }));
          setOrdersTickets((prev) => ({
            ...prev,
            [notFetchedYet]: !data.error
              ? parseDevelonProducts({
                  tickets: ticketsWithSlot,
                  loggedUser,
                  locale,
                  needUnparsedTicket: true,
                })
              : [],
          }));
        }
        hideLoader();
      }
    };
    if (orderAccordionsOpens.length) tryFetchOrderTickets();
  }, [orderAccordionsOpens]);

  const parseOrderDate = (order) => {
    if (order && order.invoice_datetime) {
      const handMadeFormat = order.invoice_datetime.replace(' ', 'T');
      try {
        const _date = format(new Date(handMadeFormat), 'dd/MM/yyyy');
        return _date;
      } catch (e) {
        return format(new Date(), 'dd/MM/yyyy');
      }
    }
  };

  const handleAccordionChange = (orderId) => {
    setOrderAccordionOpens((prev) =>
      prev.includes(orderId)
        ? [...prev.filter((oId) => oId !== orderId)]
        : [...prev, orderId]
    );
  };

  const onDownloadTicket = async (ticket) => {
    showLoader();
    try {
      const pdf = await network.ticketPdfLambda({
        ticketId: ticket.id,
        scope: 'tickets',
        federationId: loggedUser?.salId,
      });
      if (pdf) {
        const fileName = ticket.isMerch
          ? `${ticket.description.split(' ').join('_')}`
          : `${ticket.owner}_ticket`;

        downloadPDF(pdf.body, fileName);
      }
    } catch (error) {
      console.log(error);
    }
    hideLoader();
  };

  const downloadPDF = async (pdf, fileName) => {
    const source = `data:application/pdf;base64,${pdf}`;
    const link = document.createElement('a');
    link.href = source;
    link.download = `${fileName}.pdf`;
    return link.click();
  };

  const onDownloadAll = async (orderId) => {
    showLoader();
    try {
      const pdf = await network.ticketPdfLambda({
        ticketId: orderId,
        scope: 'orders',
        federationId: loggedUser?.salId,
      });
      downloadPDF(pdf.body, `order_${orderId}_ticket`);
    } catch (error) {
      console.error(error);
    }
    hideLoader();
  };

  const onCompleteOrder = (order) => {
    if (order?.number && order?.payment_info?.intent_id) {
      const path = window.location.pathname;
      window.location.href = `${backHomeLink}/${locale}${
        path?.includes('app') ? '/app' : ''
      }/sdm-tickets/buy?payment_id=${
        order.payment_info.intent_id
      }&order_number=${order.number}`;
    }
  };

  const getTicketsAvailableDates = async (ticket) => {
    const startDate = `${new Date().getFullYear()}-01-01`;
    const endDate = `${new Date().getFullYear()}-12-31`;
    const res = await network.ticketingLambda({
      params: `/calendars/availability?area_code=${ticket.product_snap.area_code}&pax_number=1&start_date=${startDate}&end_date=${endDate}`,
      customMethod: 'get',
      data: {},
      federationId: loggedUser?.salId,
    });

    return !res.error && res.slots
      ? res.slots.map((s) => ({
          date: s.date,
          groupCodes: s.groups.map((g) => g.code),
          areaCode: res.code,
        }))
      : [];
  };

  const closeDialog = () => setIsDialogOpen(false);

  const onDateChange = async (dates) => {
    const new_reservation_details = dates
      .filter((d) => !!d.selected)
      .map((date) => ({
        reservation_date: date.date,
        area_code: date.areaCode,
        group_code: date.groupCodes[0],
      }));
    const tickets = [ticketToEditDates.id];
    const oldReservation = ticketToEditDates.unparsedTicket.reservation_snap;
    const old_reservation_number = oldReservation.number;
    const dates_to_remove = oldReservation.reservation_dates.map((d) => ({
      group_id: d.group_id,
      reservation_start_date_time: d.reservation_start_date_time,
    }));
    const input = {
      params: '/reservations/move',
      customMethod: 'post',
      data: {
        new_reservation_details,
        dates_to_remove,
        tickets,
        old_reservation_number,
      },
    };
    const res = await network.ticketingLambda({
      params: '/reservations/move',
      customMethod: 'post',
      data: input,
      federationId: loggedUser?.salId,
    });
    if (res.status === 'confirmed') {
      location.reload();
    }
    hideLoader();
  };

  const onEditTicketDate = (ticket) => {
    useTicketsStore.setState({ ticketToEditDates: ticket });
    setIsDialogOpen(true);
  };

  const renderActionContainer = (ticket) => (
    <Stack
      flexDirection={'row'}
      justifyContent={isMdDown ? 'flex-start' : 'flex-end'}
      alignItems={'center'}
      gap={'30px'}
      flexWrap={'wrap'}
      paddingLeft={isMdDown ? '0px' : '5px'}
    >
      {!ticket.isMerch ? (
        <RedLinkButton
          onClick={() => onEditTicketDate(ticket)}
          msg={i18nCommon.editDate}
          icon={<ModeEditOutlineOutlined fontSize="small" color="primary" />}
        />
      ) : null}
      <RedLinkButton
        onClick={() => onDownloadTicket(ticket)}
        msg={i18nCommon.download[ticket.isMerch ? 'merch' : 'ticket']}
        icon={<ExpandMoreIcon fontSize="small" color="primary" />}
      />
    </Stack>
  );

  return (
    <Stack>
      <WhitePageContainer>
        <InsidePageContainer>
          <Typography
            variant={isMdDown ? 's' : 'm'}
            textTransform={'uppercase'}
            textAlign={'center'}
          >
            {i18n.subtitle}
          </Typography>
          <Typography variant={'xxl'} textAlign={'center'}>
            {i18n.title}
          </Typography>
          <Typography
            variant="body1"
            textAlign={'center'}
            sx={{ marginTop: '20px' }}
          >
            {i18n.textSubtitle}
          </Typography>
        </InsidePageContainer>
      </WhitePageContainer>
      {isLoaderActive && !orders?.length ? null : (
        <AllinedPageContainer>
          <InsidePageContainer gap={'25px'}>
            {orders.length
              ? orders
                  .sort((a, b) => {
                    const firstDate =
                      a.invoice_datetime.replace(' ', 'T') || '';
                    const secondDate =
                      b.invoice_datetime.replace(' ', 'T') || '';
                    return new Date(secondDate) - new Date(firstDate);
                  })
                  .map((o, idx) => (
                    <StyledAccordion
                      sx={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                      }}
                      key={idx}
                      showBefore={idx !== 0}
                      onChange={() => handleAccordionChange(o.id)}
                      expanded={orderAccordionsOpens.includes(o.id)}
                    >
                      <StyledAccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="medium" />}
                        aria-controls="panel1a-content"
                        id={`panel1a-header-${idx}`}
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            margin: '0px !important',
                          },
                        }}
                      >
                        <Stack
                          flexDirection={'row'}
                          gap={'15px'}
                          alignItems={'center'}
                        >
                          <Typography variant="m-bold">
                            {i18n.order.title} {o.number} {i18n.order.of}{' '}
                            {parseOrderDate(o)}
                          </Typography>
                          <OrderStatusLabel
                            bgColor={o?.isPending ? '#FFF4CC' : '#E8FCF3'}
                            minWidth={o?.isPending ? '78px' : null}
                          >
                            <Typography variant="s-bold">
                              {o?.isPending
                                ? i18n.order.pending
                                : i18n.order.confirmed}
                            </Typography>
                          </OrderStatusLabel>
                        </Stack>
                      </StyledAccordionSummary>
                      <AccordionDetails sx={{ padding: 0, marginTop: '0px' }}>
                        <Stack
                          direction={'column'}
                          gap={isMdDown ? '20px' : '25px'}
                        >
                          <Stack
                            flexDirection={'row'}
                            justifyContent={'flex-end'}
                            width={'100%'}
                            paddingRight={isSmDown && o.isPending ? 0 : '20px'}
                          >
                            {!o.isPending ? (
                              <RedLinkButton
                                onClick={() => onDownloadAll(o.id)}
                                msg={i18nCommon.downloadAll}
                                icon={
                                  <ExpandMoreIcon
                                    fontSize="small"
                                    color="primary"
                                  />
                                }
                                marginTop={'25px'}
                              />
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => onCompleteOrder(o)}
                                sx={{ ...(isSmDown && { width: '100%' }) }}
                              >
                                {i18nLegacy.completeOrder}
                              </Button>
                            )}
                          </Stack>
                          {Object.keys(ordersTickets).length &&
                          Object.keys(ordersTickets).includes(o.id) &&
                          ordersTickets[o.id]?.length
                            ? ordersTickets[o.id].map((t, idx) => (
                                <TicketBoughtCard
                                  key={idx}
                                  ticket={t}
                                  locale={locale}
                                  actionsContainer={
                                    !o.isPending ? (
                                      renderActionContainer(t)
                                    ) : (
                                      <></>
                                    )
                                  }
                                />
                              ))
                            : null}
                        </Stack>
                      </AccordionDetails>
                    </StyledAccordion>
                  ))
              : null}
            {orderPage >= 0 && orders.length >= 5 ? (
              <>
                <SdmSpacer height={30} />
                <Stack
                  flexDirection={'row'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  width={'100%'}
                >
                  <RedLinkButton
                    msg={i18n.showMore}
                    onClick={() => setOrderPage((prev) => prev + 1)}
                  />
                </Stack>
                <SdmSpacer height={30} />
              </>
            ) : null}
          </InsidePageContainer>
        </AllinedPageContainer>
      )}
      <EditTicketDatesDialog
        isOpen={isDialogOpen}
        onClose={() => closeDialog()}
        onAccept={onDateChange}
        loggedUser={loggedUser}
        locale={locale}
      />
    </Stack>
  );
};
export default PersonalOrders;
